<script setup lang="ts">
import { RoleGroups } from '@/constants/Roles';
import { useApiDocsService } from '@/services/api/apiDocsService';
import { ApiReference, type ReferenceProps } from '@scalar/api-reference';
import '@scalar/api-reference/style.css';
import { ENV } from 'varlock/env';
import { onUnmounted } from 'vue';

definePage({
  name: 'ApiDocs',
  meta: {
    title: 'layout.apiDocsTitle',
    roles: RoleGroups.ApiDocs
  }
});

const { documentUrl, authorizedFetch } = useApiDocsService();

// Scalar fetches the document and sends its test requests through authorizedFetch, as the signed-in user.
const configuration: ReferenceProps['configuration'] = {
  url: documentUrl,
  customFetch: authorizedFetch,
  servers: [ { url: ENV.API_URL } ],
  // The document's JWT scheme (JwtBearerDefaults.AuthenticationScheme). Left without a token: authorizedFetch adds
  // the current one, and a prefilled token would go stale and stop it refreshing.
  authentication: { preferredSecurityScheme: 'Bearer' },
  // Axios (as the client uses) and C# HttpClient only.
  defaultHttpClient: { targetKey: 'js', clientKey: 'axios' },
  hiddenClients: {
    c: true, clojure: true, dart: true, fsharp: true, go: true, http: true, java: true, julia: true, kotlin: true,
    node: true, objc: true, ocaml: true, php: true, powershell: true, python: true, r: true, ruby: true, rust: true,
    shell: true, swift: true,
    js: [ 'fetch', 'jquery', 'ofetch', 'xhr' ],
    csharp: [ 'restsharp' ]
  },
  hideClientButton: true,
  hideDarkModeToggle: false,
  forceDarkModeState: 'light',
  defaultOpenFirstTag: false,
  showDeveloperTools: 'never',
  agent: { disabled: true },
  withDefaultFonts: false,
  telemetry: false,
  mcp: { disabled: true },
  operationsSorter: 'alpha',
  tagsSorter: 'alpha',
  orderSchemaPropertiesBy: 'preserve',
  // The sidebar and search list the paths; an operation's page still shows its one-line summary.
  operationTitleSource: 'path'
};

// Scalar puts its color mode on <body>, where its global styles would keep applying to the rest of the app.
onUnmounted(() => document.body.classList.remove('dark-mode', 'light-mode'));
</script>

<template>
  <ApiReference :configuration="configuration" />
</template>

<style lang="scss">
// Gray instead of Scalar's white, to set the reference apart from the app around it. On the class Scalar puts on
// <body> (unlayered, so above its own theme layer), so the variables it derives from these, such as the sidebar
// background, follow.
.light-mode {
  --scalar-background-1: var(--bs-gray-200);
  --scalar-background-2: var(--bs-gray-100);
  --scalar-background-3: var(--bs-gray-300);
  --scalar-border-color: var(--bs-gray-400);
}

// Bootstrap's Reboot styles bare elements (h1 { font-size: … }, p { margin-bottom: … }, a { color: … }), and those
// selectors outrank Scalar's own reset, which is scoped with :where(.scalar-app) and so has no specificity at all.
// These rules restate that reset for the elements Reboot touches. An element plus :not(svg) is specificity 0,0,2:
// above Reboot's element selectors whatever the load order, below every class-based Scalar style (0,1,0 and up).
// Not scoped: Scalar's markup is not this component's.
:where(.scalar-app) :is(h1, h2, h3, h4, h5, h6, p, ol, ul, dl, dt, dd, blockquote, address, figure, pre, code, kbd,
samp, small, mark, sub, sup, b, strong, a, abbr, caption, th, legend, hr):not(svg) {
  margin: unset;
  padding: unset;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  font-style: inherit;
  line-height: inherit;
  color: inherit;
  text-align: inherit;
  text-decoration: inherit;
}

:where(.scalar-app) :is(kbd, mark):not(svg) {
  background-color: unset;
  border-radius: unset;
}

:where(.scalar-app) :is(sub, sup):not(svg) {
  position: unset;
  vertical-align: unset;
}

:where(.scalar-app) :is(code):not(svg) {
  word-wrap: unset;
}

:where(.scalar-app) :is(hr):not(svg) {
  border-width: 0;
  opacity: unset;
}

:where(.scalar-app) :is(legend):not(svg) {
  float: unset;
  width: unset;
}

:where(.scalar-app) :is(label):not(svg) {
  display: unset;
}
</style>
